import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { BILLING, MICROS_PER_CENT } from '@lib/constants/billing'
import { formatCents, formatMicros, parseDollarsToCents } from '@lib/helpers/money'
import { createPurchaseInputSchema } from '@lib/input/billing.input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useBillingActions } from '@/hooks/useBilling'

const PRESETS = [
  { cents: 2_000, label: '$20' },
  { cents: 5_000, label: '$50' },
  { cents: 10_000, label: '$100' },
] as const

/** Why an amount typed in dollars can't be bought. */
function amountMessage(cents: number | null): string {
  if (cents === null) return 'Enter an amount like 50 or 50.00.'
  if (cents < BILLING.purchaseMinCents) return `Enter at least ${formatCents(BILLING.purchaseMinCents)}.`
  return `Enter at most ${formatCents(BILLING.purchaseMaxCents)}.`
}

interface BuyCreditFormProps {
  /** The price of one live envelope now, for the "about N envelopes" line. */
  priceMicros: number
  onCancel: () => void
}

/** Mounted each time the dialog opens, so it starts empty. */
function BuyCreditForm({ priceMicros, onCancel }: BuyCreditFormProps) {
  const { createPurchase } = useBillingActions()
  const [amount, setAmount] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  // Stays true while the browser leaves for Stripe, so the button keeps spinning.
  const [leaving, setLeaving] = React.useState(false)
  const cents = parseDollarsToCents(amount)
  const valid = createPurchaseInputSchema.safeParse({ amountCents: cents ?? Number.NaN }).success
  const busy = createPurchase.isPending || leaving

  function envelopesLine(): string | null {
    if (!valid || cents === null) return null
    const envelopes = Math.floor((cents * MICROS_PER_CENT) / priceMicros)
    return `About ${envelopes.toLocaleString()} live envelopes at today's price of ${formatMicros(priceMicros)}.`
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = createPurchaseInputSchema.safeParse({ amountCents: cents ?? Number.NaN })
    if (!parsed.success) {
      setError(amountMessage(cents))
      return
    }
    setError(null)
    setFormError(null)
    try {
      const purchase = await createPurchase.mutateAsync(parsed.data)
      if (!purchase.checkoutUrl) {
        setFormError('Stripe didn’t open a payment page. Try again.')
        return
      }
      setLeaving(true)
      window.location.assign(purchase.checkoutUrl)
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not start the payment.'))
    }
  }

  const estimate = envelopesLine()
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <DialogHeader>
        <DialogTitle>Buy credit</DialogTitle>
        <DialogDescription>You&apos;ll pay by card on Stripe&apos;s checkout page, then come back here.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <Button
            key={preset.cents}
            type="button"
            variant="secondary"
            size="sm"
            aria-pressed={cents === preset.cents}
            onClick={() => {
              setAmount(String(preset.cents / 100))
              setError(null)
            }}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      <Field
        label="Amount"
        htmlFor="buy-credit-amount"
        error={error}
        hint={`From ${formatCents(BILLING.purchaseMinCents)} to ${formatCents(BILLING.purchaseMaxCents)}. Sales tax is added at checkout where it applies.`}
      >
        <Input
          id="buy-credit-amount"
          inputMode="decimal"
          value={amount}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldMessageId('buy-credit-amount')}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      {estimate ? <p className="text-caption text-muted-foreground tabular-nums">{estimate}</p> : null}
      {formError ? <p className="text-body text-danger">{formError}</p> : null}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : null}
          Continue to payment
        </Button>
      </DialogFooter>
    </form>
  )
}

interface BuyCreditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  priceMicros: number
}

/** An Owner picks an amount, then pays for it on Stripe Checkout; Stripe sends them back to Billing. */
export function BuyCreditDialog({ open, onOpenChange, priceMicros }: BuyCreditDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <BuyCreditForm priceMicros={priceMicros} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
